import { useMemo } from "react";

import { cn } from "@/lib/utils";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";

export type RiskTab =
  | "dashboard"
  | "snapshots"
  | "signals"
  | "methodology";

interface RiskTabsProps {
  value: RiskTab;
  onChange: (value: RiskTab) => void;
}

// Each tab maps to a translation key so the tab strip re-renders
// on a locale switch.
const TAB_KEY: Record<RiskTab, TranslationKey> = {
  dashboard: "risk.tabs.dashboard",
  snapshots: "risk.tabs.snapshots",
  signals: "risk.tabs.signals",
  methodology: "risk.tabs.methodology",
};

export function RiskTabs({ value, onChange }: RiskTabsProps) {
  const { t } = useTranslation();

  const options = useMemo(
    () =>
      (
        ["dashboard", "snapshots", "signals", "methodology"] as const
      ).map((tab) => ({ value: tab, label: t(TAB_KEY[tab]) })),
    [t]
  );

  return (
    <div
      role="tablist"
      className="flex gap-1 overflow-x-auto border-b border-ocean-100"
    >
      {options.map((opt) => {
        const isActive = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(opt.value)}
            className={cn(
              "shrink-0 border-b-2 px-4 py-2 text-sm font-medium transition-colors",
              isActive
                ? "border-ocean-600 text-ocean-900"
                : "border-transparent text-neutral-500 hover:text-ocean-800"
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
